import { Link } from "react-router-dom";
import PromoVideo from "../components/marketing/PromoVideo";
/* eslint-disable react/no-unescaped-entities */

const Feature = ({ title, description, icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 text-white" style={{ backgroundColor: "#00305E" }}>
      {icon}
    </div>
    <h3 className="font-bold text-slate-900 mb-1.5">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
  </div>
);

export default function MarketingPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketing-page">
      {/* Hero */}
      <header className="text-white" style={{ backgroundColor: "#00305E" }}>
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <img src="/logo_full.png" alt="SMARTIS Pro" className="mx-auto h-20 w-auto mb-6 bg-white/10 rounded-lg p-2" />
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            The all-in-one platform built for your school
          </h1>
          <p className="text-base md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            SMARTIS Pro is purpose-built for <span className="font-semibold text-white">Therapeutic, Independent, and Private Schools</span> — bringing attendance, assessments, scheduling, clinical plans, and reporting together in one secure place.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:admin@smartispro.com?subject=Interested in SMARTIS Pro for our school"
              data-testid="marketing-cta-email"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white text-[#00305E] font-semibold hover:bg-white/90 transition-colors"
            >
              Request a Demo
            </a>
            <Link
              to="/login"
              data-testid="marketing-back-to-login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Promo video */}
      <section className="bg-[#00305E]">
        <div className="max-w-4xl mx-auto px-6 pb-16 -mt-2">
          <PromoVideo />
          <p
            data-testid="woman-built-owned-tagline"
            className="text-center text-white font-semibold tracking-wide mt-6"
          >
            Woman built. Woman owned.
          </p>
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Designed for schools like yours</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Every school runs a little differently. SMARTIS Pro adapts to your programs, blocks, groups, and clinical
            workflows — so your staff spend less time on paperwork and more time with students.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Feature
            title="Attendance & Head Count"
            description="Daily attendance, tardies, early departures, and a live in-building head count for safety drills — with expected-day accuracy."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}
          />
          <Feature
            title="Assessments & Progress"
            description="Rubric-based assessments, proficiency tracking, and polished progress reports that reflect real student growth over time."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>}
          />
          <Feature
            title="Scheduling & Sign-Ups"
            description="A flexible master schedule with blocks, groups, trips, and sign-ups — plus advisory tools your staff will actually use."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
          />
          <Feature
            title="Clinical & Support Plans"
            description="Track clinical plans, communications, and support needs with heightened, role-based access controls that respect FERPA."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/></svg>}
          />
          <Feature
            title="Communications & Reminders"
            description="Log parent, district, and staff contact, assign follow-up tasks, and get in-app reminders so nothing falls through the cracks."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
          />
          <Feature
            title="Reports & Compliance"
            description="Generate the attendance, billing, and compliance reports your school and districts need — branded and ready to share."
            icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>}
          />
        </div>
      </section>

      {/* Customization CTA */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
            We'll customize SMARTIS Pro for your school
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6 leading-relaxed">
            New to SMARTIS Pro? We work directly with therapeutic, independent, and private schools to tailor the
            platform to your programs, terminology, and workflows. Reach out and we'll show you how it can work for you.
          </p>
          <div className="inline-flex flex-col items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-8 py-6">
            <p className="text-sm text-slate-500">Get in touch with us directly:</p>
            <a
              href="mailto:admin@smartispro.com"
              data-testid="marketing-email"
              className="text-lg md:text-xl font-semibold text-[#00305E] hover:underline"
            >
              admin@smartispro.com
            </a>
            <a href="tel:5704928463" className="text-sm text-slate-600 hover:underline">570-492-8463</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-6 py-10 text-center text-sm text-slate-500">
        <p className="mb-2">SMARTIS Pro Software · 801 Samson Rd. Swanton, VT 05488</p>
        <p>
          <Link to="/login" className="text-[#00305E] hover:underline">Login</Link>
          <span className="mx-2">·</span>
          <Link to="/privacy" className="text-[#00305E] hover:underline">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}
